import { useEffect, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { useParams } from "react-router-dom";
import { usePermissionValidator } from "../../../api/UserPermissionValidators";
import { useNavigate } from "react-router-dom";

import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

import { CompanyUpdateInput, DataResidency } from "../../../api/Companies";
import { useCompanyUpdate, useGetCompanyEx } from "../../../api/CompaniesApi";
import { useFamaFlags } from "../../../api/FamaFlags";

import LoadingSpinner from "../../../shared-components/LoadingSpinner";
import Toggle from "../../../shared-components/Toggle";
import CompanyUpdateToggle from "../../companyEdit/CompanyUpdateToggle";
import InputField from "../../../shared-components/InputField";
import TextAreaField from "../../../shared-components/TextAreaField";
import Button from "../../../shared-components/Button";
import CompanySettingsFieldSection from "./CompanySettingsFieldSection";

import {
  ConfirmDialogActions,
  useConfirmationDialog,
} from "../../../shared-components/ConfirmationDialog";
import { useBlocker } from "../../../utils/blocker";
import { Transition } from "history";
import {
  findInvalidBannerNotesCharacter,
  formatInvalidBannerNotesCharacter,
} from "./bannerNotesValidation";

const NOTES_TOOLTIP =
  "This text will be displayed to analysts and investigators as company notes during all misconduct and 360 workflow. This note is not visible to external customers.";

const BANNER_TOOLTIP =
  "This text is displayed on PDF and the Report Summary for all 360 reports only.";

const EU_RESIDENCY_TOOLTIP =
  "When enabled, new person data for this company and its sub-companies is stored in the EU region. When disabled, it is stored in the US region.";

const SUB_COMPANY_RESIDENCY_TOOLTIP =
  "Sub-companies always use their parent company's data residency. Change it on the parent company.";

const defaultLocals: CompanyUpdateInput = {
  companyUUID: "",
  updates: new Map(),
};

const properties: string[] = [
  "consent_forms",
  "additional_web_search",
  "web_content_translation",
  "internal_qa",
  "client_qa",
  "chat_support",
  "cqa_autocomplete_days",
  "notes",
  "banner_notes",
  "settings",
  "multilanguage",
  "lens_topics",
];
const settingsKeys: string[] = ["HAS_CUSTOM_PDF_TEMPLATE"];

const GeneralSettings = () => {
  const { companyId, subid } = useParams();
  const activeId: string = !!subid ? subid : companyId ?? "";
  const { companies, subcompanies } = usePermissionValidator();
  const [readOnly, setReadOnly] = useState<boolean>(true);
  const [locals, setLocals] = useState<CompanyUpdateInput>(defaultLocals);
  const [dirty, setDirty] = useState<boolean>(false);
  const [bannerValidationError, setBannerValidationError] = useState<
    string | null
  >(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedResidency, setSavedResidency] = useState<DataResidency>("US");
  const [transition, setTransition] = useState<Transition | undefined>(
    undefined
  );
  const localsRef = useRef(locals);
  localsRef.current = locals;

  const navigate = useNavigate();
  const { v3EuResidenceShowToggle } = useFamaFlags();

  const hasViewPermission = !!subid
    ? subcompanies.canViewSettings
    : companies.canViewSettings;

  if (!hasViewPermission) {
    navigate("/unauthorized", { replace: true });
  }

  const {
    isLoading: loadingCompanyEx,
    data: companyEx,
    mutate: loadCompanyEx,
  } = useGetCompanyEx();
  const { isLoading: loadingSave, mutateAsync: companySave } =
    useCompanyUpdate();

  useEffect(() => {
    if (locals.companyUUID !== defaultLocals.companyUUID) {
      return;
    }
    loadCompanyEx(activeId);
    let newLocals = { ...locals };
    newLocals.companyUUID = activeId;
    setLocals(newLocals);
  }, [activeId, loadCompanyEx, setLocals, locals]);

  useEffect(() => {
    if (activeId === subid) {
      setReadOnly(!subcompanies.canEditDetails);
    } else {
      setReadOnly(!companies.canEditDetails);
    }
  }, [activeId, subid, setReadOnly, companies, subcompanies]);

  useEffect(() => {
    if (!companyEx) {
      setLocals(defaultLocals);
      return;
    }
    const values = new Map<string, any>();
    properties.forEach((property) => {
      if (property === "settings") {
        let settingObj: { [key: string]: any } = {};
        settingsKeys.forEach((settingKey) => {
          const converter = new Map<string, any>([
            ["True", true],
            ["true", true],
            ["False", false],
            ["false", false],
          ]);
          settingObj[settingKey] =
            converter.get(companyEx.settings[settingKey]) ??
            companyEx.settings[settingKey];
        });
        values.set("settings", settingObj);
      } else {
        const value = companyEx[property as keyof typeof companyEx];
        values.set(property, property === "banner_notes" ? value ?? "" : value);
      }
    });
    const newLocals: CompanyUpdateInput = {
      companyUUID: companyEx?.external_id,
      updates: values,
    };
    setLocals(newLocals);
    setSavedResidency(companyEx.data_residency ?? "US");
  }, [companyEx]);

  const isSubCompany = !!companyEx?.parent_company_id;
  const pendingResidency: DataResidency | undefined =
    locals.updates.get("data_residency");
  const residency: DataResidency = pendingResidency ?? savedResidency;

  function updateResidency(isEU: boolean) {
    const newResidency: DataResidency = isEU ? "EU" : "US";
    const newLocals: CompanyUpdateInput = { ...locals };
    if (newResidency === savedResidency) {
      newLocals.updates.delete("data_residency");
    } else {
      newLocals.updates.set("data_residency", newResidency);
    }
    setLocals(newLocals);
    setDirty(true);
  }

  function updateState(key: string, value: any) {
    let newLocals: CompanyUpdateInput = { ...locals };
    if (key.includes(".")) {
      const chunks = key.split(".");
      let fullValue = newLocals.updates.get(chunks[0]);
      fullValue[chunks[1]] = value;
      newLocals.updates.set(chunks[0], fullValue);
    } else {
      newLocals.updates.set(key, value);
    }
    setLocals(newLocals);
    setDirty(true);
    if (key === "banner_notes") {
      setBannerValidationError(null);
    }
  }

  async function saveChanges() {
    if (!readOnly) {
      const bannerText = locals.updates.get("banner_notes") ?? "";
      const invalidChar = findInvalidBannerNotesCharacter(bannerText);
      if (invalidChar !== undefined) {
        setBannerValidationError(
          `Please remove invalid text: ${formatInvalidBannerNotesCharacter(
            invalidChar
          )}`
        );
        return;
      }
      setBannerValidationError(null);
      setSaveError(null);
      const residencyToSave = pendingResidency;
      const savedAtClick = savedResidency;
      companySave(locals)
        .then(() => {
          if (!residencyToSave) {
            return;
          }
          // The toggle stays enabled while the request is in flight. Commit
          // only the residency this save persisted, and keep a newer choice
          // in updates so the next save sends it.
          const current = localsRef.current;
          const pendingNow = current.updates.get("data_residency") as
            | DataResidency
            | undefined;
          unstable_batchedUpdates(() => {
            if (pendingNow === residencyToSave) {
              const nextUpdates = new Map(current.updates);
              nextUpdates.delete("data_residency");
              setLocals({ ...current, updates: nextUpdates });
              setSavedResidency(residencyToSave);
              return;
            }
            setSavedResidency(residencyToSave);
            if (pendingNow === undefined && savedAtClick !== residencyToSave) {
              const nextUpdates = new Map(current.updates);
              nextUpdates.set("data_residency", savedAtClick);
              setLocals({ ...current, updates: nextUpdates });
              setDirty(true);
              return;
            }
            if (pendingNow !== undefined) {
              setDirty(true);
            }
          });
        })
        .catch(() => {
          setDirty(true);
          setSaveError(
            residencyToSave
              ? "Changes could not be saved. The data residency change was rejected."
              : "Changes could not be saved. Please try again."
          );
        });
      setDirty(false);
    }
  }

  const { hide, modal, show } = useConfirmationDialog();
  useEffect(() => {
    transition?.retry();
  }, [transition]);
  useBlocker((tx: Transition) => {
    if (!dirty) {
      setTransition(tx);
      return true;
    }
    show(
      "Leave without saving changes?",
      "You have unsaved changes. If you leave now, your edits will be lost.",
      (action: ConfirmDialogActions) => {
        if (action === "continue") {
          setDirty(false);
          setTransition(tx);
          return true;
        } else {
          hide();
        }
      }
    );
  });

  return (
    <div className="p-7 bg-gray-50 max-w-[800px]">
      {modal}
      <h2 className="text-xl font-medium mb-4">Company Configuration</h2>
      {loadingCompanyEx || !companyEx || !locals.updates.has(properties[0]) ? (
        <LoadingSpinner />
      ) : (
        <div>
          <div className="flex gap-x-9 pb-5">
            <div className="flex-1">
              <CompanyUpdateToggle
                readOnly={readOnly}
                obj={locals}
                callback={updateState}
                field="consent_forms"
                label="Consent Forms"
              />
              <CompanyUpdateToggle
                readOnly={readOnly}
                obj={locals}
                callback={updateState}
                field="additional_web_search"
                label="Add Additional Web Search"
              />
              <CompanyUpdateToggle
                readOnly={readOnly}
                obj={locals}
                callback={updateState}
                field="web_content_translation"
                label="Enable Web Content Translations"
              />
              <CompanyUpdateToggle
                readOnly={readOnly}
                obj={locals}
                callback={updateState}
                field="lens_topics"
                label="Enable Lens Topics"
              />
              {v3EuResidenceShowToggle && (
                <div className="mb-4 w-64">
                  <Toggle
                    defaultValue={residency === "EU"}
                    disabled={readOnly || isSubCompany}
                    label="EU Data Residency"
                    tooltip={
                      isSubCompany
                        ? SUB_COMPANY_RESIDENCY_TOOLTIP
                        : EU_RESIDENCY_TOOLTIP
                    }
                    onChange={updateResidency}
                  />
                </div>
              )}
            </div>
            <div className="flex-1">
              <CompanyUpdateToggle
                readOnly={readOnly}
                obj={locals}
                callback={updateState}
                field="internal_qa"
                label="Enable Internal Investigator"
              />
              <CompanyUpdateToggle
                readOnly={readOnly}
                obj={locals}
                callback={updateState}
                field="client_qa"
                label="Enable Client Investigator"
              />
              <CompanyUpdateToggle
                readOnly={readOnly}
                obj={locals}
                callback={updateState}
                field="chat_support"
                label="Enable Live Chat Support"
              />
              <CompanyUpdateToggle
                readOnly={readOnly}
                obj={locals}
                callback={updateState}
                field="settings.HAS_CUSTOM_PDF_TEMPLATE"
                label="Has Custom PDF Template"
              />
              <CompanyUpdateToggle
                readOnly={readOnly}
                obj={locals}
                callback={updateState}
                field="multilanguage"
                label="Enable Multi-Language Screening"
              />
            </div>
            <div className="flex-1">
              {locals.updates.get("client_qa") && (
                <div className="flex">
                  <div className="flex-1">Client QA Days</div>
                  <div className="flex-1">
                    <InputField
                      value={locals.updates.get("cqa_autocomplete_days")}
                      type={"number"}
                      name={"frequency"}
                      min={1}
                      max={99}
                      classes="w-20 p-0"
                      readOnly={readOnly}
                      handleChange={(e: any) => {
                        updateState(
                          "cqa_autocomplete_days",
                          parseInt(e.target.value)
                        );
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          {v3EuResidenceShowToggle && pendingResidency && (
            <div
              role="alert"
              className="flex gap-x-3 mb-5 p-4 rounded-md border border-yellow-300 bg-yellow-50 text-yellow-800 text-sm"
            >
              <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />
              <p>
                Data residency will change from {savedResidency} to{" "}
                {pendingResidency} for this company and all of its
                sub-companies. Person information created before this change
                stays in the {savedResidency} region and might no longer be
                accessible.
              </p>
            </div>
          )}
          {((activeId === companyId && companies.canViewNotes) ||
            (activeId === subid && subcompanies.canViewNotes)) && (
            <>
              <div className="pt-5 pb-5">
                <CompanySettingsFieldSection
                  title="Notes"
                  tooltip={NOTES_TOOLTIP}
                  tooltipId="notes-tooltip"
                >
                  <TextAreaField
                    disabled={readOnly}
                    name="notes"
                    className="h-52"
                    classes="w-full"
                    placeholder="Type Here..."
                    onChange={(e) => updateState("notes", e.target.value)}
                    value={locals.updates.get("notes") ?? ""}
                  />
                </CompanySettingsFieldSection>
              </div>
              <div className="pb-5">
                <CompanySettingsFieldSection
                  title="Report Banner"
                  tooltip={BANNER_TOOLTIP}
                  tooltipId="banner-notes-tooltip"
                >
                  <TextAreaField
                    disabled={readOnly}
                    name="banner_notes"
                    className="h-[90px]"
                    classes="w-full"
                    placeholder="Type Here..."
                    onChange={(e) =>
                      updateState("banner_notes", e.target.value)
                    }
                    value={locals.updates.get("banner_notes") ?? ""}
                  />
                </CompanySettingsFieldSection>
              </div>
            </>
          )}
          <div>
            {loadingSave ? (
              <LoadingSpinner />
            ) : (
              <Button
                text="Save Changes"
                onClick={(e) => {
                  e.preventDefault();
                  saveChanges();
                }}
                className="flex-1"
                disabled={readOnly}
              />
            )}
            {bannerValidationError && (
              <p className="text-red-500 mt-2">{bannerValidationError}</p>
            )}
            {saveError && <p className="text-red-500 mt-2">{saveError}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneralSettings;
